import { ToastProvider } from '@evisort/eds/components/Toast/subcomponents/ToastProvider';
/* eslint-disable no-restricted-imports -- pre-existing Ec* overlay unrelated to this change; EDS migration is separate scope */
import * as Sentry from '@sentry/react';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { lazy, Suspense } from 'react';
import { createUseStyles } from 'react-jss';
import { Outlet } from 'react-router';
import 'react-toastify/dist/ReactToastify.css';
import { Slide, ToastContainer } from 'react-toastify';

import { useAuthenticationStore } from '~/auth/useAuthenticationStore';
import { AppConfigProvider } from '~/common/contexts/AppConfig';
import { PusherProvider } from '~/common/contexts/PusherContext';
import ErrorBoundary from '~/components/ErrorBoundary/ErrorBoundary';
import { RouterSideEffects } from '~/components/Router/RouterSideEffects';
import { Welcome } from '~/components/Welcome/Welcome';
import { isDevToolsEnabled } from '~/devtools/devToolsPreference';
import { PanelProvider } from '~/eds/src/components/Panel/PanelContext';
import { LegacyBrowserBanner } from '~/features/browser-support/components/LegacyBrowserBanner';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { MODAL_AUTH_REQUIRE_POPUP } from '~/types/modal.types';
import { getRecentFailedResources } from '~/utils/resourceTimingDiagnostics';

import { SkipToMainLink } from './SkipToMainLink';

const DevTools = isDevToolsEnabled()
  ? lazy(() => import('~/devtools/DevTools'))
  : () => null;

const EcModalNestedOverlay = lazy(
  () => import('~/components/Shared/EcModalNestedOverlay/EcModalNestedOverlay'),
);

const useGlobalToastStyles = createUseStyles({
  '@global': {
    '.toast-container': {
      width: 'min-content',
      padding: 0,
      bottom: '16px',
      right: '16px',
    },
    '.toast-message': {
      minHeight: 'auto',
      margin: 0,
      marginTop: '16px',
      padding: 0,
      borderRadius: '4px',
      fontFamily: 'unset',
    },
  },
});

/**
 * Always-on shell that lives above the route layout split. Owns the global
 * provider stack, the main landmark, accessibility scaffolding, and global
 * UI like toasts. The matched layout route (`LoggedInLayout` /
 * `LoggedOutLayout`) renders inside the `<main>` via `<Outlet />` and owns
 * the auth-conditional chrome.
 *
 * The auth-required re-auth modal lives here (not in `LoggedInLayout`) so it
 * can surface when `requiresReauth` is true but the user is no longer
 * `isLoggedIn` — `LoggedInLayout` would otherwise have redirected to /login
 * already.
 */
function AppContentsView() {
  useGlobalToastStyles();
  const { isLoggedIn: isUserLoggedIn, requiresReauth } =
    useAuthenticationStore();

  return (
    <div className="main-layout">
      <SkipToMainLink />
      <LegacyBrowserBanner />
      {(isUserLoggedIn || requiresReauth) && (
        <Suspense fallback={null}>
          <EcModalNestedOverlay
            width="580px"
            modalType={MODAL_AUTH_REQUIRE_POPUP}
          />
        </Suspense>
      )}
      <ToastContainer
        position="bottom-center"
        transition={Slide}
        hideProgressBar={true}
        autoClose={5000}
        closeButton={false}
        pauseOnHover={false}
        pauseOnFocusLoss={false}
        draggable={false}
        className="toast-container"
        toastClassName="toast-message"
        closeOnClick={false}
      />
      <Outlet />
    </div>
  );
}

function AppContents() {
  const isComponentMonitoringEnabled = useFlag(
    FlagType.ComponentMonitoringInSentry,
  );

  return (
    <NuqsAdapter>
      <Sentry.ErrorBoundary
        fallback={({ eventId }) => <ErrorBoundary eventId={eventId} />}
        beforeCapture={(scope) => {
          // Browsers only report "this resource failed" and never *why* — a nested
          // fetch (a CSS @import, an @font-face src, a worker's own imports) that
          // breaks gets attributed to whatever outer resource triggered it, not the
          // real culprit. Recent hard-failed resources are a general-purpose net for
          // that blind spot, independent of asset type, on top of the more targeted
          // diagnostics chunkLoadRecovery.ts attaches for CSS preload failures.
          const recentFailures = getRecentFailedResources();
          if (recentFailures.length > 0) {
            scope.setContext('recentFailedResources', {
              resources: recentFailures,
            });
          }
        }}
      >
        <AppConfigProvider>
          <PusherProvider>
            <PanelProvider>
              <ToastProvider>
                <Sentry.Profiler
                  name="AppContents"
                  disabled={!isComponentMonitoringEnabled}
                  updateProps={{}}
                >
                  <RouterSideEffects />
                  <AppContentsView />
                  <Suspense fallback={null}>
                    <DevTools />
                  </Suspense>
                </Sentry.Profiler>
                <Welcome />
              </ToastProvider>
            </PanelProvider>
          </PusherProvider>
        </AppConfigProvider>
      </Sentry.ErrorBoundary>
    </NuqsAdapter>
  );
}

export default AppContents;
